import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Eye, PaperPlaneTilt } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { DatePicker, TimePicker } from '@/components/ui/native-datetime';
import { toast } from 'sonner';
import { formatSubmitError } from '@/utils/formErrors';
import { useNavigate } from 'react-router-dom';
import { EMPLOYEE_NAMES as FALLBACK_NAMES, calculateHolidayHours, calculateOvertimeHours } from '@/lib/workCalculations';
import useDirectory from '@/hooks/useDirectory';
import useUsedDates from '@/hooks/useUsedDates';
import { todayISO, Req, labelCls, fieldInput, fieldTextarea, fieldTrigger, btnPrimary, btnSecondary } from '@/components/forms/formKit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  form_type: 'overtime',
  employee_names: [],
  department: '',
  date: todayISO(),
  work_type: '',
  start_time: '',
  end_time: '',
  hours: 0,
  instruction_content: '',
};

export default function OvertimeFormModal({ onClose, initialData, submissionId, embedded = false }) {
  const navigate = useNavigate();
  const { employees, employeeNames, departmentNames } = useDirectory();
  const empOptions = employeeNames.length ? employeeNames : FALLBACK_NAMES;
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(() => {
    // Hydrate from initialData (edit mode) — convert legacy `employee_name` (joined string)
    // into the array shape this form uses.
    if (!initialData) return { ...EMPTY };
    const d = { ...EMPTY, ...initialData };
    if (!Array.isArray(d.employee_names) || d.employee_names.length === 0) {
      if (typeof initialData.employee_name === 'string' && initialData.employee_name) {
        d.employee_names = initialData.employee_name.split('、').map((s) => s.trim()).filter(Boolean);
      } else {
        d.employee_names = [];
      }
    }
    return d;
  });
  const isEdit = !!submissionId;
  const usedDates = useUsedDates('overtime', formData.employee_names, submissionId);

  // Auto-calculate hours based on work type
  useEffect(() => {
    let hours = 0;
    if (formData.work_type === '休日出勤' && formData.start_time && formData.end_time) {
      hours = calculateHolidayHours(formData.start_time, formData.end_time);
    } else if (formData.work_type === '残業' && formData.end_time) {
      hours = calculateOvertimeHours(formData.end_time);
    } else if (formData.work_type === '深夜残業' && formData.end_time) {
      hours = calculateOvertimeHours(formData.end_time);
    } else if (formData.work_type === '早出' && formData.start_time && formData.end_time) {
      const [sh, sm] = formData.start_time.split(':').map(Number);
      const [eh, em] = formData.end_time.split(':').map(Number);
      const diff = (eh * 60 + em) - (sh * 60 + sm);
      hours = Math.max(0, Math.round((diff / 60) * 100) / 100);
    }
    setFormData((prev) => ({ ...prev, hours }));
  }, [formData.work_type, formData.start_time, formData.end_time]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'work_type' && value === '残業') {
        updated.start_time = '17:30';
      }
      // When department changes, drop any selected employees outside this dept
      if (field === 'department') {
        updated.employee_names = (prev.employee_names || []).filter((n) => {
          const emp = employees.find((e) => e.name === n);
          return emp && emp.department_name === value;
        });
      }
      return updated;
    });
  };

  // Employees scoped to the selected department (falls back to all if no dept yet)
  const filteredEmpOptions = formData.department
    ? employees.filter((e) => e.department_name === formData.department).map((e) => e.name)
    : empOptions;

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // Send with employee_name being the joined names for compatibility
      const submitData = {
        ...formData,
        employee_name: formData.employee_names.join('、'),
      };
      if (isEdit) {
        await axios.put(`${API}/forms/submissions/${submissionId}`, { form_data: submitData });
        toast.success('申請を更新しました');
        if (onClose) onClose();
        setTimeout(() => navigate(`/submission/${submissionId}`), 300);
      } else {
        await axios.post(`${API}/forms/submit`, submitData);
        toast.success('登録しました — 履歴に追加されました');
        setFormData({ ...EMPTY });
        setShowPreview(false);
      }
    } catch (error) {
      console.error('Submit failed:', error);
      toast.error(formatSubmitError(error, isEdit));
    } finally {
      setSubmitting(false);
    }
  };

  const isHolidayWork = formData.work_type === '休日出勤';
  const isOvertime = formData.work_type === '残業';
  const showStartTime = formData.work_type && formData.work_type !== '残業';

  const canPreview = formData.employee_names.length > 0 && formData.department && 
    formData.date && formData.work_type && formData.instruction_content && formData.end_time;

  const body = (
    <>
      <div className="sticky top-0 bg-white border-b border-zinc-100 px-6 py-5 flex items-center justify-between z-20">
        <h2 className="text-2xl font-bold tracking-tight text-zinc-900">
          勤務指示書{isEdit && <span className="ml-2 text-sm font-normal text-zinc-500">— 編集</span>}
        </h2>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-100 rounded-full transition-colors text-zinc-500 hover:text-zinc-900"
            data-testid="modal-close-btn"
          >
            <X size={24} />
          </button>
        )}
      </div>

      {!showPreview ? (
          <div className="p-6 sm:p-8 space-y-6">
            <div>
              <Label htmlFor="department" className={labelCls}>所属部署<Req /></Label>
              {departmentNames.length > 0 ? (
                <Select value={formData.department} onValueChange={(val) => handleChange('department', val)}>
                  <SelectTrigger data-testid="select-department" className={`mt-1.5 ${fieldTrigger}`}>
                    <SelectValue placeholder="所属部署を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {departmentNames.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="department"
                  data-testid="input-department"
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  className={`mt-1.5 ${fieldInput}`}
                  required
                />
              )}
            </div>
            <div>
              <Label className={labelCls}>氏名<Req /><span className="ml-2 text-xs font-normal text-zinc-500">複数選択可</span></Label>
              <MultiSelect
                options={filteredEmpOptions}
                selected={formData.employee_names}
                onChange={(names) => handleChange('employee_names', names)}
                placeholder={formData.department ? '氏名を選択してください（複数可）' : 'まず所属部署を選択してください'}
                testId="multi-select-employee-names"
                disabled={!formData.department && departmentNames.length > 0}
              />
              {formData.department && filteredEmpOptions.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">この部署に登録された社員がいません。社員管理で追加してください。</p>
              )}
            </div>
            <div>
              <Label htmlFor="date" className={labelCls}>日付<Req /></Label>
              <DatePicker
                id="date"
                testId="input-date"
                value={formData.date}
                onChange={(v) => handleChange('date', v)}
                disabledDates={usedDates}
                warnNonWorking
                required
              />
              <p className="text-xs text-zinc-500 mt-1">土日・祝日は休日出勤として扱われます</p>
            </div>
            <div>
              <Label htmlFor="work_type" className={labelCls}>勤務種別<Req /></Label>
              <Select value={formData.work_type} onValueChange={(val) => handleChange('work_type', val)}>
                <SelectTrigger data-testid="select-work-type" className={`mt-1.5 ${fieldTrigger}`}>
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="早出">早出</SelectItem>
                  <SelectItem value="残業">残業</SelectItem>
                  <SelectItem value="休日出勤">休日出勤</SelectItem>
                  <SelectItem value="深夜残業">深夜残業</SelectItem>
                  <SelectItem value="直行・直帰">直行・直帰</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isOvertime && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-900">
                <strong>残業ルール:</strong> 開始時刻は常に 17:30、上限は 19:30 まで（最大2時間カウント）
              </div>
            )}
            {isHolidayWork && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
                <strong>休日出勤ルール:</strong> 開始～終了の時間から休憩（10:00-10:05、12:00-12:40、15:00-15:10）を自動で差し引いて計算します
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {showStartTime && (
                <div>
                  <Label htmlFor="start_time" className="font-bold">開始時刻 *</Label>
                  <TimePicker
                    id="start_time"
                    testId="input-start-time"
                    value={formData.start_time}
                    onChange={(v) => handleChange('start_time', v)}
                  />
                </div>
              )}
              {isOvertime && (
                <div>
                  <Label className="font-bold">開始時刻 (固定)</Label>
                  <Input value="17:30" disabled className="border-zinc-300 bg-zinc-100" />
                </div>
              )}
              <div>
                <Label htmlFor="end_time" className="font-bold">終了時刻 *</Label>
                <TimePicker
                  id="end_time"
                  testId="input-end-time"
                  value={formData.end_time}
                  onChange={(v) => handleChange('end_time', v)}
                />
              </div>
            </div>

            {formData.work_type && formData.work_type !== '直行・直帰' && formData.end_time && (
              <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 flex items-center justify-between">
                <Label className="font-bold text-zinc-700">自動計算された時間数 (1人あたり):</Label>
                <span className="text-2xl font-bold text-teal-700" data-testid="calculated-hours">
                  {formData.hours} 時間
                </span>
              </div>
            )}

            <div>
              <Label htmlFor="instruction_content" className={labelCls}>指示内容<Req /></Label>
              <Textarea
                id="instruction_content"
                data-testid="textarea-instruction"
                value={formData.instruction_content}
                onChange={(e) => handleChange('instruction_content', e.target.value)}
                className={`mt-1.5 ${fieldTextarea}`}
                required
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={onClose} className={btnSecondary}>
                キャンセル
              </Button>
              <Button
                onClick={() => setShowPreview(true)}
                data-testid="preview-btn"
                disabled={!canPreview}
                className={btnPrimary}
              >
                <Eye size={18} className="mr-2" />
                プレビュー
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="bg-gradient-to-br from-zinc-50 to-white rounded-lg border-2 border-dashed border-zinc-200 p-6 sm:p-8 mb-6 shadow-inner">
              <h3 className="text-lg font-bold mb-4 border-b border-zinc-300 pb-2">申請内容の確認</h3>
              <dl className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">氏名 ({formData.employee_names.length}名):</dt>
                  <dd className="col-span-2 text-zinc-900">{formData.employee_names.join('、')}</dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">所属部署:</dt>
                  <dd className="col-span-2 text-zinc-900">{formData.department}</dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">日付:</dt>
                  <dd className="col-span-2 text-zinc-900">{formData.date}</dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">勤務種別:</dt>
                  <dd className="col-span-2 text-zinc-900">{formData.work_type}</dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">開始時刻:</dt>
                  <dd className="col-span-2 text-zinc-900">{formData.start_time}</dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">終了時刻:</dt>
                  <dd className="col-span-2 text-zinc-900">{formData.end_time}</dd>
                </div>
                {formData.work_type !== '直行・直帰' && (
                  <div className="grid grid-cols-3 gap-2">
                    <dt className="font-medium text-zinc-600">時間数 (1人あたり):</dt>
                    <dd className="col-span-2 text-teal-700 font-bold">{formData.hours} 時間</dd>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">指示内容:</dt>
                  <dd className="col-span-2 text-zinc-900 whitespace-pre-wrap">{formData.instruction_content}</dd>
                </div>
              </dl>
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowPreview(false)} className="border-zinc-300">
                戻る
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="preview-modal-submit-btn"
                className="bg-teal-700 hover:bg-teal-800 shadow-sm hover:shadow-md transition-all"
              >
                <PaperPlaneTilt size={18} className="mr-2" />
                {submitting ? (isEdit ? '更新中…' : '登録中…') : (isEdit ? '更新する' : '登録する')}
              </Button>
            </div>
          </div>
        )}
    </>
  );

  if (embedded) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-zinc-200 w-full overflow-hidden" data-testid="overtime-edit">
        {body}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-zinc-900/70 pointer-events-auto">
      <div className="bg-white rounded-lg shadow-xl border border-zinc-200 w-full max-w-2xl max-h-[calc(100vh-7rem)] overflow-y-auto mt-20 sm:mt-24 mx-4 mb-4">
        {body}
      </div>
    </div>
  );
}
